import { type ReactElement } from 'react';
import type { ShopperProducts } from '@/scapi';
import { Link } from '@/components/link';
import logo from '/images/logo.svg';
import { useTranslation } from 'react-i18next';
import { Region } from '@/components/region';
import type { ContentSlotPageWithComponentData } from '@/extensions/snk/lib/content-slot-page';
import { MegaMenu, type MegaMenuNavItem } from '@/features/mega-menu';
import { Search } from '../search';
import { UserActions } from './ui/molecules/user-actions';

const GLOBAL_HEADER_BANNER_REGION_ID = 'headerbanner';

const MEGA_MENU_ITEMS: MegaMenuNavItem[] = [
    { id: 'brands', label: 'Brands', to: '/brands' },
    { id: 'new-in', label: 'New In', to: '/new-in' },
    { id: 'offers', label: 'Offers', to: '/offers' },
    { id: 'inside-space', label: 'Inside Space', to: '/inside-space' },
    { id: 'trending', label: 'Trending', to: '/trending' },
];

interface GlobalHeaderProps {
    rootCategory?: Promise<ShopperProducts.schemas['Category']>;
    globalContentSlots?: Promise<ContentSlotPageWithComponentData>;
}

export const GlobalHeader = ({ rootCategory, globalContentSlots }: GlobalHeaderProps): ReactElement => {
    const { t } = useTranslation('header');

    return (
        <header className="sticky top-0 z-50 bg-snk-white" data-slot="global-header">
            <div className="flex justify-end px-4 lg:px-9">
                {globalContentSlots ? (
                    <Region
                        page={globalContentSlots}
                        regionId={GLOBAL_HEADER_BANNER_REGION_ID}
                        fallbackElement={null}
                        errorElement={null}
                    />
                ) : null}
            </div>
            <div className="flex flex-row flex-wrap w-full">
                <div className="flex-row flex justify-between w-full p-4">
                    <Link to="/" className="" data-testid="header-logo">
                        <img
                            src={logo}
                            alt={t('logoAlt')}
                            className="h-3 lg:h-4 w-auto [filter:var(--header-logo-filter)]"
                        />
                    </Link>
                    <Search />
                    <UserActions />
                </div>

                <div className="w-full mt-6">
                    <MegaMenu
                        data-slot="mega-menu"
                        items={MEGA_MENU_ITEMS}
                        submenuPanels={[]}
                        shopAllLabel="Shop app"
                        rootCategory={rootCategory}
                    />
                </div>
            </div>
        </header>
    );
};
